import { Navigate, createBrowserRouter } from 'react-router-dom';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { AppLayout } from '@/layouts/AppLayout';
import { AdminBranchesPage } from '@/pages/AdminBranchesPage';
import { AdminBrandsPage } from '@/pages/AdminBrandsPage';
import { AdminColorsPage } from '@/pages/AdminColorsPage';
import { AdminDashboardPage } from '@/pages/AdminDashboardPage';
import { CommercialDashboardPage } from '@/pages/CommercialDashboardPage';
import { AdminPhoneModelsPage } from '@/pages/AdminPhoneModelsPage';
import { AdminQuotationConfigPage } from '@/pages/AdminQuotationConfigPage';
import { CatalogPage } from '@/pages/CatalogPage';
import { CheckoutReturnPage } from '@/pages/CheckoutReturnPage';
import { CustomerAccountPage } from '@/pages/CustomerAccountPage';
import { CustomerActivateAccountPage } from '@/pages/CustomerActivateAccountPage';
import { CustomerForgotPasswordPage } from '@/pages/CustomerForgotPasswordPage';
import { CustomerReservationPage } from '@/pages/CustomerReservationPage';
import { CustomerResetPasswordPage } from '@/pages/CustomerResetPasswordPage';
import { CustomerCheckEmailPage } from '@/pages/CustomerCheckEmailPage';
import { CustomerRegisterPage } from '@/pages/CustomerRegisterPage';
import { CustomerVerifyEmailPage } from '@/pages/CustomerVerifyEmailPage';
import { DeviceDetailPage } from '@/pages/DeviceDetailPage';
import { DevicesPage } from '@/pages/DevicesPage';
import { FollowUpPage } from '@/pages/FollowUpPage';
import { HomePage } from '@/pages/HomePage';
import { LegacyLoginRedirect, LoginPage } from '@/pages/LoginPage';
import { NewQuotationPage } from '@/pages/NewQuotationPage';
import { CustomersPage } from '@/pages/CustomersPage';
import { CustomerDetailPage } from '@/pages/CustomerDetailPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PublicDevicePage } from '@/pages/PublicDevicePage';
import { QuotationDetailPage } from '@/pages/QuotationDetailPage';
import { QuotationRequestPage } from '@/pages/QuotationRequestPage';
import { QuotationsPage } from '@/pages/QuotationsPage';

export const appRouter = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'catalog',
        element: <CatalogPage />,
      },
      {
        path: 'catalog/:id',
        element: <PublicDevicePage />,
      },
      {
        path: 'quotation-request',
        element: <QuotationRequestPage />,
      },
      {
        // The single login for buyers and staff (#271).
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'account/register',
        element: <CustomerRegisterPage />,
      },
      {
        path: 'account/check-email',
        element: <CustomerCheckEmailPage />,
      },
      {
        path: 'account/verify',
        element: <CustomerVerifyEmailPage />,
      },
      {
        path: 'account/activate',
        element: <CustomerActivateAccountPage />,
      },
      {
        // The buyer's login before #271; links and bookmarks keep working, `?next=` included.
        path: 'account/login',
        element: <LegacyLoginRedirect />,
      },
      {
        path: 'account/forgot-password',
        element: <CustomerForgotPasswordPage />,
      },
      {
        path: 'account/reset-password',
        element: <CustomerResetPasswordPage />,
      },
      {
        path: 'account',
        element: (
          <ProtectedRoute allowedRoles={['CUSTOMER']}>
            <CustomerAccountPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'account/reservations/:id',
        element: (
          <ProtectedRoute allowedRoles={['CUSTOMER']}>
            <CustomerReservationPage />
          </ProtectedRoute>
        ),
      },
      {
        // Where Mercado Pago sends the buyer back (VEN-7); it asks the backend, not the URL.
        path: 'checkout/return',
        element: (
          <ProtectedRoute allowedRoles={['CUSTOMER']}>
            <CheckoutReturnPage />
          </ProtectedRoute>
        ),
      },
      {
        // Employees come in too: the dashboard shows them the operation and keeps the
        // configuration, which the backend reserves for ADMIN anyway, out of sight.
        path: 'admin',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'EMPLOYEE']}>
            <AdminDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/quotations',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'EMPLOYEE']}>
            <QuotationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/quotations/new',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'EMPLOYEE']}>
            <NewQuotationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/quotations/:id',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'EMPLOYEE']}>
            <QuotationDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/follow-up',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'EMPLOYEE']}>
            <FollowUpPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/customers',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'EMPLOYEE']}>
            <CustomersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/customers/:id',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'EMPLOYEE']}>
            <CustomerDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/devices',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'EMPLOYEE']}>
            <DevicesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/devices/:id',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'EMPLOYEE']}>
            <DeviceDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/metrics',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <CommercialDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/brands',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminBrandsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/colors',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminColorsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/branches',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminBranchesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/phone-models',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminPhoneModelsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/pricing',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminQuotationConfigPage />
          </ProtectedRoute>
        ),
      },
      {
        // The operations panel moved into Administración; old links and bookmarks follow it.
        path: 'employee',
        element: <Navigate to="/admin" replace />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
